import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import apiClient from '@/api/client'
import {
  ExerciseSetContext,
  useExerciseSlotRegistry,
  type ExerciseGrade,
} from '../hooks/useExerciseSet'
import { ExerciseSetBar } from './ExerciseSetBar'

interface ExerciseSetProviderProps {
  /** Tant que le message assistant est en cours de streaming, la barre reste masquée. */
  isStreaming: boolean
  /** Session courante — absente pendant le streaming, sert à l'enregistrement serveur. */
  sessionId: string | null
  onSendMessage?: (content: string) => void
  children: ReactNode
}

/**
 * Enregistre côté serveur la correction d'un exercice auto-corrigeable, pour que
 * `QuizAnsweredEvent` soit enfin émis (App\Domains\Learning\Services\RecordQuizAnswerHandler,
 * lot K, exposé par `QuizAnswerController`). Best-effort : un échec réseau ne doit
 * jamais bloquer le parcours de l'élève, la correction est déjà affichée côté
 * client au moment de l'appel. `!grade.answered` est exclu : un exercice
 * auto-corrigeable laissé vide n'est pas une réponse fausse, c'est une absence de
 * réponse — l'enregistrer gonflerait `quiz_success_rate` d'une erreur qui n'existe
 * pas (voir la JSDoc du champ `answered`).
 */
function recordQuizAnswer(sessionId: string, grade: ExerciseGrade): void {
  if (grade.correct === null || !grade.answered) return
  apiClient
    .post(`/sessions/${sessionId}/quiz-answers`, {
      correct: grade.correct,
      question: grade.question,
    })
    .catch(() => {
      // best-effort, voir la JSDoc ci-dessus.
    })
}

/**
 * Collecte tous les blocs ```exercise d'un même message assistant en une série
 * (DEFAUTS-EXERCICES.md, D1). Monté une fois par bulle assistant par `ChatBubble`,
 * autour du rendu markdown : chaque composant d'exercice s'y inscrit à son montage
 * via `useExerciseSlot` et met à jour sa réponse localement, sans rien envoyer. La
 * validation groupée, déclenchée depuis `ExerciseSetBar`, verrouille la série,
 * construit le message unique du contrat de soumission et l'envoie en un seul
 * appel à `onSendMessage`.
 */
export function ExerciseSetProvider({
  isStreaming,
  sessionId,
  onSendMessage,
  children,
}: Readonly<ExerciseSetProviderProps>) {
  const { t } = useTranslation()
  const [locked, setLocked] = useState(false)
  const [lockedSummary, setLockedSummary] = useState({ correct: 0, gradable: 0 })
  const { registerSlot, unregisterSlot, getSortedGrades, total, answered } =
    useExerciseSlotRegistry()

  const handleValidate = useCallback(() => {
    const grades = getSortedGrades()
    if (grades.length === 0) return

    let correctCount = 0
    let gradableCount = 0
    const lines = [t('session.exercise_set_header')]

    grades.forEach((grade, i) => {
      const index = i + 1
      // Un exercice auto-corrigeable laissé vide (chemin « valider quand
      // même ») a `correct: false` par construction, mais ce n'est pas une
      // réponse fausse : c'est une absence de réponse. On le traite comme un
      // exercice non corrigé — même ligne, hors score — plutôt que de
      // compter l'omission comme une erreur (voir la JSDoc d'`answered`).
      const isGraded = grade.correct !== null && grade.answered

      if (!isGraded) {
        lines.push(
          t('session.exercise_set_line_ungraded', {
            index,
            itemType: grade.itemType,
            question: grade.question,
            answer: grade.answerLabel,
          }),
        )
      } else {
        gradableCount += 1
        if (grade.correct) correctCount += 1
        lines.push(
          t('session.exercise_set_line_graded', {
            index,
            itemType: grade.itemType,
            question: grade.question,
            answer: grade.answerLabel,
            verdict: grade.correct
              ? t('session.exercise_set_verdict_correct')
              : t('session.exercise_set_verdict_incorrect'),
          }),
        )
      }

      if (sessionId) recordQuizAnswer(sessionId, grade)
    })

    if (gradableCount > 0) {
      lines.push(t('session.exercise_set_score', { correct: correctCount, total: gradableCount }))
    }

    setLockedSummary({ correct: correctCount, gradable: gradableCount })
    setLocked(true)
    onSendMessage?.(lines.join('\n'))
  }, [getSortedGrades, onSendMessage, sessionId, t])

  const ctxValue = useMemo(
    () => ({ locked, registerSlot, unregisterSlot }),
    [locked, registerSlot, unregisterSlot],
  )

  return (
    <ExerciseSetContext.Provider value={ctxValue}>
      {children}
      {!isStreaming && total > 0 && (
        <ExerciseSetBar
          total={total}
          answered={answered}
          locked={locked}
          correctCount={lockedSummary.correct}
          gradableCount={lockedSummary.gradable}
          onValidate={handleValidate}
        />
      )}
    </ExerciseSetContext.Provider>
  )
}
